import { useState, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { useGetExpectedAllocation, useGetOrders, useGetB2BOrders } from '@/services/outbound/outboundWaitingStatus/useOutboundWaitingStatusQuries'
import { GetOrdersReq, B2BGetOrdersReq, TabType } from '@/services/outbound/outboundWaitingStatus/types'
import { GRID_SIZE } from '@/utils/grid/grid'
import { omit } from 'lodash-es'
import { B2BOutboundForm } from '../components/B2BContentWrapper'

// B2C용 hook
export function useB2COutboundData(queryKey: GetOrdersReq & { isAllSelected: Record<string, boolean> }) {
  const [currentPage, setCurrentPage] = useState(0)

  const {
    data,
    refetch: refetchOrders,
    isFetching: isFetchingOrders,
  } = useGetOrders({
    ...omit(queryKey, 'tabType', 'isAllSelected'),
    shipperNames: queryKey.isAllSelected.shipperNames ? null : queryKey.shipperNames || [],
    logisticsServiceTypes: queryKey.isAllSelected.logisticsServiceTypes ? null : queryKey.logisticsServiceTypes || [],
    page: currentPage,
    size: GRID_SIZE.MEDIUM,
    cutType: 'B2C',
  })

  const {
    data: expectedAllocationData,
    refetch: refetchAllocation,
    isFetching: isFetchingAllocation,
  } = useGetExpectedAllocation({
    logisticsServiceType: queryKey.isAllSelected.logisticsServiceTypes ? null : queryKey.logisticsServiceTypes?.[0] || '',
    shipperNames: queryKey.isAllSelected.shipperNames ? null : queryKey.shipperNames || [],
    cutType: 'B2C',
  })

  const refetchAllData = useCallback(async () => {
    return await Promise.all([refetchOrders(), refetchAllocation()])
  }, [refetchOrders, refetchAllocation])

  const onChangePage = useCallback(
    async (page: number) => {
      flushSync(() => setCurrentPage(page))
      await refetchAllData()
    },
    [refetchAllData],
  )

  return {
    data,
    expectedAllocationData,
    currentPage,
    setCurrentPage,
    isLoading: isFetchingOrders || isFetchingAllocation,
    refetchAllData,
    onChangePage,
  }
}

// B2B용 hook
export function useB2BOutboundData(queryKey: B2BOutboundForm & { isAllSelected: Record<string, boolean> }) {
  const [currentPage, setCurrentPage] = useState(0)

  const b2BOrderParams = {
    ...omit(queryKey, 'tabType', 'isAllSelected'),
    shipperNames: queryKey.isAllSelected.shipperNames ? null : queryKey.shipperNames || [],
    logisticsServiceType: queryKey.isAllSelected.logisticsServiceTypes ? null : queryKey.logisticsServiceTypes?.[0] || '',
    page: currentPage,
    size: GRID_SIZE.V2_DEFAULT,
    cutType: 'B2B',
  } as ApiPaginationReq<B2BGetOrdersReq>

  const { data, refetch: refetchOrders, isFetching: isFetchingOrders } = useGetB2BOrders(b2BOrderParams)

  const {
    data: expectedAllocationData,
    refetch: refetchAllocation,
    isFetching: isFetchingAllocation,
  } = useGetExpectedAllocation({
    logisticsServiceType: queryKey.isAllSelected.logisticsServiceTypes ? null : queryKey.logisticsServiceTypes?.[0] || '',
    shipperNames: queryKey.isAllSelected.shipperNames ? null : queryKey.shipperNames || [],
    cutType: 'B2B',
  })

  const refetchAllData = useCallback(async () => {
    return await Promise.all([refetchOrders(), refetchAllocation()])
  }, [refetchOrders, refetchAllocation])

  const onChangePage = useCallback(
    async (page: number) => {
      flushSync(() => setCurrentPage(page))
      await refetchAllData()
    },
    [refetchAllData],
  )

  return {
    data,
    expectedAllocationData,
    currentPage,
    setCurrentPage,
    isLoading: isFetchingOrders || isFetchingAllocation,
    refetchAllData,
    onChangePage,
  }
}
